import { enums, resources } from '../gads'
import { GadsFields, GadsFieldsRequired } from '../utils/global'

// Bid changes at an ad_group criterion or campaign criterion level
export interface AdGroupCriterionChangeStatus {
    change_status: Pick<
        resources.ChangeStatus,
        | 'resource_name'
        | 'ad_group_criterion'
        | 'last_change_date_time'
        | 'resource_status'
        | 'resource_type'
    >
}

export interface CampaignCriterionChangeStatus {
    change_status: Pick<
        resources.ChangeStatus,
        | 'resource_name'
        | 'campaign_criterion'
        | 'last_change_date_time'
        | 'resource_status'
        | 'resource_type'
    >
}

export interface AdGroupCriterion {
    ad_group: Pick<resources.AdGroup, 'resource_name' | 'id'>
    ad_group_criterion: Pick<
        resources.AdGroupCriterion,
        | 'bid_modifier'
        | 'cpc_bid_micros'
        | 'resource_name'
        | 'type'
        | 'keyword'
        | 'age_range'
        | 'gender'
        | 'income_range'
        | 'parental_status'
    >
}

export interface CampaignCriterion {
    campaign: Pick<resources.Campaign, 'resource_name' | 'id'>
    campaign_criterion: Pick<resources.CampaignCriterion, 'bid_modifier' | 'resource_name' | 'type'>
}

export interface Bid {
    last_change_date_time: string
    criterion_resource_name: string
    entity_id: number
    resource_type: enums.ChangeStatusResourceType
    resource_status: enums.ChangeStatusOperation
    bid_change: number
    criterion_type: enums.CriterionType
    criterion: string
    change_type: BidChangeType
}

export interface CurrentBid {
    resource_name: string
    value: number
    change_ts: string
    criterion_type: number
    criterion: string
}

export interface BidChange {
    domain_id: number
    resource_name: string
    resource_status: enums.ChangeStatusOperation
    value: number
    change_ts: string
    criterion_type: enums.CriterionType
    criterion: string
    change_type: string
}

export enum BidChangeType {
    BID = 'BID',
    BID_MOD = 'BID_MOD',
    FIRST_LOG = 'FIRST_LOG',
}

export interface ResourceBidChange {
    resource_name: string
    value: number
    change_ts: string
    unix_ts: number
    days_since_change: number
    criterion_type: number
    criterion: string
    change_type: BidChangeType
}

// Target changes at an ad_group or campaign level
export interface AdGroup {
    ad_group: GadsFieldsRequired<
        resources.AdGroup,
        'resource_name' | 'effective_target_cpa_source' | 'effective_target_roas_source'
    > &
        GadsFields<
            resources.AdGroup,
            | 'target_cpa_micros'
            | 'target_roas'
            | 'effective_target_cpa_micros'
            | 'effective_target_roas'
        >
    campaign: GadsFieldsRequired<resources.Campaign, 'resource_name' | 'bidding_strategy_type'>
}

export interface Campaign {
    campaign: GadsFieldsRequired<resources.Campaign, 'resource_name' | 'bidding_strategy_type'> &
        GadsFields<resources.Campaign, 'target_cpa' | 'target_roas'>
}

export enum TargetChangeType {
    TARGET = 'TARGET',
    FIRST_LOG = 'FIRST_LOG',
}

export interface Target {
    last_change_date_time: string
    entity_resource_name: string
    resource_type: enums.ChangeStatusResourceType.AD_GROUP | enums.ChangeStatusResourceType.CAMPAIGN
    resource_status: enums.ChangeStatusOperation
    bidding_strategy_target: number
    bidding_strategy_target_type: enums.BiddingStrategyType
    change_type: TargetChangeType
}

export interface CurrentTarget {
    resource_name: string
    value: number
    change_ts: string
    bidding_strategy_target_type: number
}

export interface TargetChange extends CurrentTarget {
    domain_id: number
    resource_status: enums.ChangeStatusOperation
    change_type: TargetChangeType
}

export interface ResourceTargetChange extends CurrentTarget {
    unix_ts: number
    days_since_change: number
    change_type: TargetChangeType
}

// Objects for both Bid and Target
export namespace BidsTargetsPairs {
    export enum LogsKeys {
        BIDS = 'all_logged_bids',
        TARGETS = 'all_logged_targets',
    }

    enum FormattedKeys {
        BIDS = 'formatted_bid_changes',
        TARGETS = 'formatted_target_changes',
    }

    export interface LoggedData {
        [LogsKeys.BIDS]: CurrentBid[]
        [LogsKeys.TARGETS]: CurrentTarget[]
    }

    export interface FormattedChanges {
        [FormattedKeys.BIDS]: BidChange[]
        [FormattedKeys.TARGETS]: TargetChange[]
    }
}
